/*
    Examiner credit-transaction history page.

    Surfaces the examiner's current credit balance alongside a read-only ledger of every
    transaction recorded against the credit pools they belong to — report charges
    (transaction_recordReport) and admin credit top-ups (transaction_editCredit). Newest-first,
    as returned by POST /transaction/dir.
*/

import React, { useCallback, useEffect, useState } from 'react';
import Dashboard from '../dashboard';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Button, Card, Result, Statistic, Table, Typography } from 'antd';
import styles from '../../style/credits-page.module.css';

const { Title } = Typography;

const columns = [
  { dataIndex: 'date', key: 'date', title: 'Date' },
  { dataIndex: 'type', key: 'type', title: 'Type' },
  { dataIndex: 'report', key: 'report', title: 'Report' },
  { dataIndex: 'records', key: 'records', title: 'Records' },
  { dataIndex: 'amount', key: 'amount', title: 'Credits' }
];

const toRow = (tx) => {
  const date = tx.date ? new Date(tx.date).toLocaleDateString() : '';

  if (tx.kind === 'transaction_editCredit') {
    const amount = tx.amount ?? 0;
    return {
      key: tx._id,
      date,
      type: 'Credit added',
      report: '—',
      records: '—',
      amount: `${amount >= 0 ? '+' : ''}${amount}`
    };
  }

  // transaction_recordReport
  const price = tx.price ?? 0;
  return {
    key: tx._id,
    date,
    type: 'Report',
    report: tx.report ? tx.report.name : '—',
    records: (tx.records || []).map((record) => record.label).join(', ') || '—',
    amount: price === 0 ? 'Free' : `-${price}`
  };
};

const ExaminerCreditsPage = () => {
  const token = useSelector((state) => state.token);
  const [credits, setCredits] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Token ' + token
      }
    };

    setLoading(true);
    setError(false);

    try {
      const [poolsResponse, txResponse] = await Promise.all([
        axios.post(process.env.REACT_APP_API_URL + '/credit-pool/dir', {}, config),
        axios.post(process.env.REACT_APP_API_URL + '/transaction/dir', {}, config)
      ]);

      const pools = poolsResponse.data.creditPools || [];
      setCredits(pools.reduce((sum, pool) => sum + (pool.credits || 0), 0));
      setTransactions(txResponse.data.transactions || []);
    } catch (err) {
      console.log(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) load();
  }, [load, token]);

  return (
    <Dashboard>
      <div className={styles['page']}>
        <Title className={styles['title']} level={3}>
          Credits
        </Title>

        <Card className={styles['balanceCard']} loading={loading}>
          <Statistic title="Credits available" value={credits} />
        </Card>

        <Title className={styles['sectionTitle']} level={5}>
          Transaction history
        </Title>

        {error ? (
          <Result
            status="warning"
            title="Couldn't load your transactions."
            subTitle="Please try again."
            extra={
              <Button type="primary" onClick={load}>
                Retry
              </Button>
            }
          />
        ) : (
          <Table
            columns={columns}
            dataSource={transactions.map(toRow)}
            loading={loading}
            locale={{ emptyText: 'No transactions yet' }}
            pagination={false}
          />
        )}
      </div>
    </Dashboard>
  );
};

export default ExaminerCreditsPage;
